// Componente ForgotPassword para recuperación de contraseña
// Permite al usuario solicitar un email de restablecimiento de contraseña

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from '../../services/authService';
import LayoutSignUp from '../../components/UI/LayoutSignUp';
import AmarsiLogo from '../../components/Icons/AmarsiLogo';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import toast from 'react-hot-toast';

/**
 * Componente ForgotPassword
 * 
 * Formulario para solicitar recuperación de contraseña
 * El usuario ingresa su email y recibe un link para restablecerla
 */
const ForgotPassword = () => {
  // Estado del formulario
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Hooks
  const navigate = useNavigate();

  // Valida el formato del email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Maneja el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Limpiar errores previos
    setError('');

    // Validar email
    if (!email.trim()) {
      setError('El email es requerido');
      return;
    }

    if (!validateEmail(email)) {
      setError('El email no es válido');
      return;
    }

    setIsSubmitting(true);

    try {
      // Llamar a la función de envío de email de recuperación
      // Usa la Edge Function send-email con branding Amarsi
      await sendPasswordResetEmail(email);

      setEmailSent(true);
      toast.success('Email enviado correctamente. Revisa tu bandeja de entrada.');
    } catch (err) {
      console.error('Error en recuperación de contraseña:', err);
      setError(err.message || 'Ocurrió un error al procesar tu solicitud');
      toast.error('Error al procesar la solicitud');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LayoutSignUp withProgressBar={false}>
      <div className="p-6">
        <div className="text-center mb-6">
          <AmarsiLogo width="80" height="80" className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">
            ¿Olvidaste tu contraseña?
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400 mt-2">
            {emailSent 
              ? '¡Email enviado! Revisa tu bandeja de entrada.'
              : 'Ingresá tu email y te enviaremos un link para restablecerla.'
            }
          </p>
        </div>

        {!emailSent ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Campo Email */}
            <Input
              type="email"
              label="Correo electrónico"
              placeholder="Tu correo electrónico"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={error}
              icon="email"
              autoFocus
              disabled={isSubmitting}
            />

            {/* Botón de envío */}
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={!email || isSubmitting}
              size="md"
              className="w-full"
              variant="primary"
            >
              Enviar email de recuperación
            </Button>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-sm text-green-800 dark:text-green-200">
                Hemos enviado un email a <strong>{email}</strong> con instrucciones para restablecer tu contraseña.
              </p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-2">
                El link expirará en 1 hora.
              </p>
            </div>

            <Button
              onClick={() => setEmailSent(false)}
              size="md"
              className="w-full"
              variant="secondary"
            >
              Enviar otro email
            </Button>
          </div>
        )}

        {/* Botón para volver al login */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/auth/login')}
            className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-500 font-medium"
          >
            ← Volver al inicio de sesión
          </button>
        </div>
      </div>
    </LayoutSignUp>
  );
};

export default ForgotPassword;
